if (!customElements.get('cart-gift-options')) {
  class CartGiftOptions extends HTMLElement {
    constructor() {
      super();
      this.onGiftToggle = this.onGiftToggle.bind(this);
      this.onGiftWrapToggle = this.onGiftWrapToggle.bind(this);
      this.onGiftMessageInput = debounce(this.onGiftMessageInput.bind(this), ON_CHANGE_DEBOUNCE_TIMER);
      this.lastSavedMessage = '';
    }

    connectedCallback() {
      if (this.dataset.giftReady === 'true') return;
      this.dataset.giftReady = 'true';

      this.giftToggle = this.querySelector('[data-gift-order-toggle]');
      this.giftWrapToggle = this.querySelector('[data-gift-wrap-toggle]');
      this.giftDetails = this.querySelector('[data-gift-details]');
      this.giftMessage = this.querySelector('[data-gift-message]');
      this.summary = this.querySelector('[data-gift-summary]');
      this.summaryOrder = this.querySelector('[data-gift-summary-order]');
      this.summaryWrap = this.querySelector('[data-gift-summary-wrap]');
      this.summaryMessageRow = this.querySelector('[data-gift-summary-message-row]');
      this.summaryMessage = this.querySelector('[data-gift-summary-message]');
      this.status = this.querySelector('[data-gift-status]');
      this.error = this.querySelector('[data-gift-error]');

      if (!this.giftToggle || !this.giftWrapToggle || !this.giftDetails || !this.giftMessage) return;

      this.giftToggle.addEventListener('change', this.onGiftToggle);
      this.giftWrapToggle.addEventListener('change', this.onGiftWrapToggle);
      this.giftMessage.addEventListener('input', this.onGiftMessageInput);
      this.lastSavedMessage = this.giftMessage.value.trim();

      this.updateVisibility();
      this.updateSummary();
    }

    disconnectedCallback() {
      this.giftToggle?.removeEventListener('change', this.onGiftToggle);
      this.giftWrapToggle?.removeEventListener('change', this.onGiftWrapToggle);
      this.giftMessage?.removeEventListener('input', this.onGiftMessageInput);
    }

    onGiftToggle() {
      if (!this.giftToggle.checked) {
        this.giftWrapToggle.checked = false;
        this.giftMessage.value = '';
      }

      this.updateVisibility();
      this.updateSummary();
      this.syncGiftState();
    }

    onGiftWrapToggle() {
      this.updateSummary();
      this.syncGiftState();
    }

    onGiftMessageInput() {
      if (!this.giftToggle.checked || this.isSubmitting) return;
      this.updateSummary();
      this.saveGiftMessage();
    }

    updateVisibility() {
      this.giftDetails.hidden = !this.giftToggle.checked;
    }

    updateSummary() {
      if (!this.summary) return;

      const isGift = this.giftToggle.checked;
      const messageText = this.giftMessage.value.trim();

      this.summary.hidden = !isGift;

      if (this.summaryOrder) {
        this.summaryOrder.textContent = isGift ? 'Yes' : 'No';
      }

      if (this.summaryWrap) {
        this.summaryWrap.textContent = isGift && this.giftWrapToggle.checked ? 'Added' : 'Not added';
      }

      if (this.summaryMessageRow) {
        this.summaryMessageRow.hidden = messageText.length === 0;
      }

      if (this.summaryMessage) {
        this.summaryMessage.textContent = messageText;
      }
    }

    setBusy(isBusy) {
      this.isSubmitting = isBusy;
      this.classList.toggle('is-loading', isBusy);
      this.querySelectorAll('input, textarea').forEach((field) => {
        field.disabled = isBusy;
      });
    }

    setStatus(message, state = '') {
      if (!this.status) return;

      this.status.textContent = message || '';
      this.status.dataset.state = state || '';
      this.status.hidden = !message;
    }

    setError(message) {
      if (!this.error) return;
      this.error.hidden = !message;
      this.error.textContent = message || '';
    }

    getCartRoute(routeKey, fallback) {
      const route = window.routes && routes[routeKey] ? routes[routeKey] : fallback;
      return route.endsWith('.js') ? route : `${route}.js`;
    }

    getFetchConfig() {
      if (typeof fetchConfig === 'function') return fetchConfig();

      return {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      };
    }

    getSectionsToRender() {
      const sections = [];
      const mainCartItems = document.getElementById('main-cart-items');
      const mainCartFooter = document.getElementById('main-cart-footer');
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      const cartLiveRegion = document.getElementById('cart-live-region-text');
      const cartDrawer = document.getElementById('CartDrawer');

      if (mainCartItems?.dataset.id) {
        sections.push({
          containerId: 'main-cart-items',
          sectionId: mainCartItems.dataset.id,
          selector: '.js-contents',
        });
      }

      if (mainCartFooter?.dataset.id) {
        sections.push({
          containerId: 'main-cart-footer',
          sectionId: mainCartFooter.dataset.id,
          selector: '.cart__footer',
        });
      }

      if (cartIconBubble) {
        sections.push({
          containerId: 'cart-icon-bubble',
          sectionId: 'cart-icon-bubble',
          selector: '.shopify-section',
        });
      }

      if (cartLiveRegion) {
        sections.push({
          containerId: 'cart-live-region-text',
          sectionId: 'cart-live-region-text',
          selector: '.shopify-section',
        });
      }

      if (cartDrawer) {
        sections.push({
          containerId: 'CartDrawer',
          sectionId: 'cart-drawer',
          selector: '.drawer__inner',
        });
      }

      return sections;
    }

    getAttributePayload() {
      const isGift = this.giftToggle.checked;
      const giftWrapping = isGift && this.giftWrapToggle.checked;
      const giftMessage = isGift ? this.giftMessage.value.trim() : '';

      return {
        'Is this order a gift': isGift ? 'Yes' : 'No',
        'Gift wrapping': giftWrapping ? 'Yes' : 'No',
        'Gift message': giftMessage,
      };
    }

    async postJson(url, body) {
      const response = await fetch(url, {
        ...this.getFetchConfig(),
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Gift cart request failed: ${response.status}`);
      }

      const responseText = await response.text();

      try {
        return JSON.parse(responseText);
      } catch (error) {
        return responseText;
      }
    }

    updateCartContainers(parsedState) {
      if (!parsedState || !parsedState.sections) return;

      const itemCount = typeof parsedState.item_count === 'number' ? parsedState.item_count : null;
      if (itemCount !== null) {
        document.querySelector('cart-items')?.classList.toggle('is-empty', itemCount === 0);
        document.querySelector('cart-drawer')?.classList.toggle('is-empty', itemCount === 0);
        document.getElementById('main-cart-footer')?.classList.toggle('is-empty', itemCount === 0);
      }

      this.getSectionsToRender().forEach((section) => {
        const html = parsedState.sections[section.sectionId];
        if (!html) return;

        const container = document.getElementById(section.containerId);
        if (!container) return;

        const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
        const target = container.querySelector(section.selector) || container;

        if (source && target) {
          target.innerHTML = source.innerHTML;
        }
      });
    }

    async syncGiftState() {
      if (this.isSubmitting) return;

      const isGift = this.giftToggle.checked;
      const giftWrappingWanted = isGift && this.giftWrapToggle.checked;
      const giftWrapInCart = this.dataset.giftWrapInCart === 'true';
      const giftWrapLine = Number(this.dataset.giftWrapLine || 0);
      const giftWrapVariantId = this.dataset.giftWrapVariantId;
      const sections = this.getSectionsToRender().map((section) => section.sectionId);

      this.setError('');
      this.setStatus('Saving gift options...', 'saving');
      this.setBusy(true);

      try {
        if (!giftWrappingWanted && giftWrapInCart && giftWrapLine > 0) {
          await this.postJson(this.getCartRoute('cart_change_url', '/cart/change'), {
            line: giftWrapLine,
            quantity: 0,
          });
        }

        if (giftWrappingWanted && !giftWrapInCart) {
          await this.postJson(this.getCartRoute('cart_add_url', '/cart/add'), {
            items: [
              {
                id: giftWrapVariantId,
                quantity: 1,
              },
            ],
          });
        }

        const updatedCart = await this.postJson(this.getCartRoute('cart_update_url', '/cart/update'), {
          attributes: this.getAttributePayload(),
          sections,
          sections_url: window.location.pathname,
        });

        this.updateCartContainers(updatedCart);
        this.setStatus('Gift options saved.', 'success');
      } catch (error) {
        console.error(error);
        this.setError('We could not save your gift options. Please try again.');
        this.setStatus('', '');
      } finally {
        this.setBusy(false);
      }
    }

    async saveGiftMessage() {
      const nextMessage = this.giftToggle.checked ? this.giftMessage.value.trim() : '';
      if (this.isMessageSaving || nextMessage === this.lastSavedMessage) return;

      this.isMessageSaving = true;
      this.setError('');
      this.setStatus('Saving gift message...', 'saving');

      try {
        await this.postJson(this.getCartRoute('cart_update_url', '/cart/update'), {
          attributes: this.getAttributePayload(),
        });
        this.lastSavedMessage = nextMessage;
        this.setStatus('Gift message saved.', 'success');
      } catch (error) {
        console.error(error);
        this.setError('We could not save your gift message. Please try again.');
        this.setStatus('', '');
      } finally {
        this.isMessageSaving = false;
        const currentMessage = this.giftToggle.checked ? this.giftMessage.value.trim() : '';
        if (currentMessage !== this.lastSavedMessage) {
          this.saveGiftMessage();
        }
      }
    }
  }

  customElements.define('cart-gift-options', CartGiftOptions);
}
